// Performance charts for the printable progress report (report-view/UsersView).
//
// Data encoding: each plotted value packs two facts into one number —
// the integer part is the session score, the first decimal is the group code
// (1..5 -> E, D, C, B, A). e.g. 70.3 = score 70, group C;  -6.9 = score -7,
// group E. A decimal of 0 means "no group recorded for this session".
//
// The report is printed in black & white, so every distinction has to survive
// grayscale: the score line is a dark solid line with markers, the trend is a
// light dotted line, and the data labels are outlined pills. Colour is only an
// accent on top of a design that already works without it.

const GROUP_LETTERS = ["E", "D", "C", "B", "A"];

// Positive fractional part, so the group code survives negative scores:
// -6.9 - Math.floor(-6.9) === 0.1 -> code 1 -> "E". Reading `val % 1` instead
// returns -0.9 and loses the letter, which is why negative sessions used to
// print a meaningless "E-".
export function scoreOf(val) {
  return Math.floor(val);
}

export function groupOf(val) {
  const code = Math.round((val - Math.floor(val)) * 10);
  return GROUP_LETTERS[code - 1] || "";
}

// "70 · C" when the session has a group, plain "70" when it does not.
export function formatScoreLabel(val) {
  if (val === null || val === undefined || Number.isNaN(val)) return "";
  const group = groupOf(val);
  return group ? `${scoreOf(val)} · ${group}` : `${scoreOf(val)}`;
}

/**
 * Pack each session's score together with the group the client was in for it.
 *
 * `changeHistory` only records the sessions where the group *changed*, so it is
 * usually shorter than the score series. A missing entry means "still in the
 * same group", not "no group" — carrying the last one forward is what keeps the
 * later sessions from printing a bare number with no letter. The final session
 * always shows the client's current group.
 *
 * @param {number[]} scores          one score per session
 * @param {number[]} changeHistory   recorded groups (1 = A .. 5 = E)
 * @param {number} currentGroup      the client's group today (1 = A .. 5 = E)
 * @returns {number[]} score + groupCode/10, ready to plot and to label
 */
export function packSessionValues(scores, changeHistory, currentGroup) {
  const history = (changeHistory || []).map(group => 6 - group);
  const current = currentGroup ? 6 - currentGroup : 0;
  const lastIndex = scores.length - 1;
  let code = history[0] || current;

  return scores.map((score, i) => {
    // history[i + 1] is the group recorded for session i, when there is one.
    if (history[i + 1]) code = history[i + 1];
    const sessionCode = i === lastIndex ? current || code : code;
    return score + sessionCode / 10;
  });
}

const INK = "#1f2937"; // near-black: axis text, reference lines
const MUTED = "#64748b"; // axis labels
const GRID = "#dcdfe5"; // pale horizontal rules at every y tick (20, 40, 60 …)
const TREND = "#9aa3af"; // dotted trend: light enough to read as "behind"

// One chart per category. `accent` colours the score line, its markers and the
// label pill border — all chosen dark enough to stay legible as gray. `tint`
// fills the label pill and prints as near-white.
export function buildReportChartOptions({ accent = INK, tint = "#f8fafc" } = {}) {
  return {
    chart: {
      type: "line",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    tooltip: { enabled: false },
    // series 0 = score, series 1 = trend
    colors: [accent, TREND],
    stroke: {
      curve: "smooth",
      width: [2.5, 4],
      // The trend is turned into dots by CSS (stroke-dasharray + round caps);
      // ApexCharts cannot express a dash shorter than its gap on its own.
      dashArray: [0, 0],
      lineCap: "round",
    },
    markers: {
      size: [3.5, 0],
      colors: [accent],
      strokeColors: ["#ffffff"],
      strokeWidth: 1.5,
      hover: { size: 0 },
    },
    dataLabels: {
      // Only the score line is labelled; the trend is a shape, not a reading.
      enabled: true,
      enabledOnSeries: [0],
      textAnchor: "middle",
      offsetY: -8,
      formatter: formatScoreLabel,
      // ApexCharts fills the pill with the label's text colour and then repaints
      // the text with `background.foreColor` — hence the inversion below.
      style: { fontSize: "9px", fontWeight: 700, colors: [tint] },
      background: {
        enabled: true,
        foreColor: INK,
        padding: 4,
        borderRadius: 3,
        borderWidth: 1,
        borderColor: accent,
        opacity: 1,
      },
    },
    grid: {
      borderColor: GRID,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      // Left/right room so the first and last label pills sit inside the plot
      // instead of straddling the axis.
      padding: { top: 0, right: 24, bottom: 0, left: 22 },
    },
    xaxis: {
      axisBorder: { show: true, color: GRID },
      axisTicks: { show: false },
      labels: {
        style: { colors: MUTED, fontSize: "9px", fontWeight: 600 },
      },
    },
    yaxis: {
      min: -20,
      max: 120,
      // 20-point steps: enough to read a value off the grid without the
      // 14-line thicket the chart used to print.
      tickAmount: 7,
      position: "back",
      labels: {
        show: true,
        offsetX: -14,
        style: { colors: MUTED, fontSize: "9px", fontWeight: 600 },
        formatter: val => parseInt(val, 10),
      },
    },
    legend: { show: false },
    annotations: {
      position: "back",
      yaxis: [
        // The 0 and 100 boundaries of the target range. Deliberately lines and
        // not a filled band: ApexCharts paints annotations on top of the grid,
        // so a fill here would hide the pale 20/40/60… lines behind it.
        { y: 0, borderColor: INK, borderWidth: 1.5, strokeDashArray: 0 },
        { y: 100, borderColor: INK, borderWidth: 1.5, strokeDashArray: 0 },
      ],
    },
  };
}

// Kept for the callers that still spread the plain object (Test/Report/TestCompare).
export const lineChartOptions = buildReportChartOptions();
